import { useSelector, useDispatch } from "react-redux";
import { RootState } from "store/reducers";
import { useEffect, useCallback } from "react";
import {
  RemoteParticipant,
  connect,
} from "twilio-video";
import { appRegx, isHostingApp } from "utils";
import { removeErrorNotification, setErrorNotification } from "store/errors/slice";
import { ErrorLevel, ErrorType } from "store/errors/types";
import {
  addRemoteParticipant,
  removeRemoteParticipant,
  clearRemoteParticipants,
} from "store/remote-participants/slice";
import { setRoom } from "store/room/slice";
import rollbar from "helpers/rollbar";

export function useRoom(roomUid: string) {
  const dispatch = useDispatch();
  const localTracks = useSelector((s: RootState) => s.localTracks);
  const { twilioToken } = useSelector((s: RootState) => s.user);
  const { room } = useSelector((s: RootState) => s.room);

  /** Create initial local tracks, connect to the twilio room, and set up
   * twilio event listeners for participantConnected and participantDisonnected.
   * Also, set up room-service subscriptions for lists, which we will use
   * to update our local participant's subscribeRules.
   */
  const startRoom = useCallback(
    async (roomUid: string, twilioToken: string) => {
      dispatch(clearRemoteParticipants());

      try {
        if (localTracks.localAudioTrack && localTracks.localVideoTrack && localTracks.localDataTrack) {
          if (localTracks.hideVideo) {
            localTracks.localVideoTrack.disable();
          }

          if (localTracks.muteAudio) {
            localTracks.localAudioTrack.disable();
          }
          const newRoom = await connect(twilioToken, {
            name: roomUid,
            tracks: [localTracks.localVideoTrack, localTracks.localAudioTrack, localTracks.localDataTrack],
            bandwidthProfile: {
              video: {
                mode: "grid",
                dominantSpeakerPriority: "standard",
                renderDimensions: {
                  high: { height: 720, width: 1280 },
                  standard: { height: 720, width: 1280 },
                  low: { height: 480, width: 858 },
                },
                maxTracks: 2,
              },
            },
            dominantSpeaker: true,
            networkQuality: { local: 1, remote: 0 },
          });

          const participantConnected = (participant: RemoteParticipant) => {
            const notHostingApp = /^Switcher Studio App$/g.test(
              participant.identity
            );
            if (notHostingApp) {
              // If the app participant joins with "Host from this Device" disabled,
              // the identity will not be appended with a uuid, and we should
              // ignore it in the room.
              return;
            }
  
            // If hosts already exists in the room and a new host connects,
            // remove the original one from our application state.
            const newParticipantIsHost = appRegx.test(participant.identity);
            const currentlyConnectedHosts = Array.from(
              newRoom.participants.values()
            ).filter(isHostingApp);
  
            if (newParticipantIsHost && currentlyConnectedHosts.length) {
              currentlyConnectedHosts.forEach(c => {
                if (c.identity !== participant.identity) {
                  dispatch(removeRemoteParticipant(c.sid));
                }
              })
            }
  
            console.log("[useRoom.ts] - Participant connected: ", participant);
            dispatch(
              addRemoteParticipant({
                sid: participant.sid,
                identity: participant.identity,
              })
            );

          };
          const participantDisconnected = (participant: RemoteParticipant) => {
            console.log("[useRoom.ts] - Participant disconnected: ", participant);
            dispatch(removeRemoteParticipant(participant.sid));
          };

          newRoom.on("participantConnected", participantConnected);
          newRoom.on("participantDisconnected", participantDisconnected);
          newRoom.participants.forEach(participantConnected); // ?
          newRoom.once("disconnected", (_, err) => {
            if (err) {
              console.error(err);
              dispatch(
                setErrorNotification({
                  name: err.code,
                  type: ErrorType.DisconnectionError,
                  showButton: true,
                  message: err.message,
                })
              );
            }
          });

          newRoom.on('reconnecting', error => {
            dispatch(
              setErrorNotification({
                name: `Error ${error.code}`,
                type: ErrorType.DisconnectionError,
                level: ErrorLevel.Warning,
                message: "There's a problem with your connection. Attempting to reconnect...",
              })
            );
          });

          newRoom.on('reconnected', () => {
            dispatch(removeErrorNotification());
          });

          window.addEventListener("beforeunload", () => newRoom.disconnect());
          window.addEventListener("pagehide", () => newRoom.disconnect());

          dispatch(setRoom(newRoom));
        }
      } catch (err) {
        if ("code" in err) {
          dispatch(
            setErrorNotification({
              name: err.code,
              type: ErrorType.ConnectionError,
              showButton: true,
              message: err.message,
            })
          );
          rollbar.error(err);
        } else {
          dispatch(
            setErrorNotification({
              name: err.name,
              type: ErrorType.MediaError,
              showButton: true,
              message: err.message,
            })
          );
          rollbar.error(err);
        }
      }
    },
    [
      dispatch,
      localTracks.muteAudio,
      localTracks.hideVideo,
      localTracks.localAudioTrack,
      localTracks.localVideoTrack,
      localTracks.localDataTrack
    ]
  );

  useEffect(() => {
    if (!room) {
      startRoom(roomUid, twilioToken);
    }
  }, [room, roomUid, twilioToken, startRoom]);

  return { room };
}
